import React from 'react';

import type {
  EvidenceStatus,
  IntakeFormData,
} from '@/components/intakes/form/types';

import {
  computeCoverageSubRows,
  computeEvidenceDefaults,
} from '../form/evidenceDefaults';

/** Narrowed key type for keys in `form.evidenceStatuses`. */
type EvidenceKey = keyof IntakeFormData['evidenceStatuses'];

/** Local UI state for insurance sub-rows (PIP, Health, BI). */
export type EvidenceSubs = {
  pip: EvidenceStatus;
  health: EvidenceStatus;
  thirdParty: EvidenceStatus;
};

/** Parameters for `useEvidenceReviewState`. */
interface UseEvidenceReviewStateParams {
  open: boolean;
  form: IntakeFormData;
  initialStatuses: IntakeFormData['evidenceStatuses'];
}

/** Return shape for `useEvidenceReviewState`. */
interface UseEvidenceReviewStateReturn {
  statuses: IntakeFormData['evidenceStatuses'];
  handleChange: (key: EvidenceKey, value: EvidenceStatus) => void;
  insuranceSubs: EvidenceSubs;
  setInsuranceSubs: React.Dispatch<React.SetStateAction<EvidenceSubs>>;
}

/**
 * useEvidenceReviewState
 *
 * Handles seeding from form + initialStatuses, and manages local state for
 * top-level evidence rows and insurance sub-rows.
 *
 * Behavior:
 * - When the dialog opens, compute defaults from the intake `form` and then
 *   selectively preserve user overrides for a small set of keys
 *   (`photos`, `witnesses`, `policeReport`, `ems`, `medicalTreatment`).
 *   An override is considered when the saved value is `UNCERTAIN` or
 *   `EXISTS_NOT_HAVE` and the computed value is not `CONFIRMED`. Notes are also
 *   preserved.
 * - Insurance sub-rows (PIP/Health/BI) are seeded from `computeCoverageSubRows`
 *   and merged with any existing notes in `initialStatuses` if present.
 * - Seeding is gated by `seededRef` so it only occurs once per open cycle; the
 *   flag resets when `open` becomes false.
 */
export const useEvidenceReviewState = ({
  open,
  form,
  initialStatuses,
}: UseEvidenceReviewStateParams): UseEvidenceReviewStateReturn => {
  const [statuses, setStatuses] = React.useState(initialStatuses);
  const [insuranceSubs, setInsuranceSubs] = React.useState<EvidenceSubs>({
    pip: { status: 'UNCERTAIN', notes: '' },
    health: { status: 'UNCERTAIN', notes: '' },
    thirdParty: { status: 'UNCERTAIN', notes: '' },
  });

  const seededRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current) return;
    seededRef.current = true;

    const computed = computeEvidenceDefaults(form);
    const next = { ...computed } as typeof computed;

    const preserveKeys: ReadonlyArray<EvidenceKey> = [
      'photos',
      'witnesses',
      'policeReport',
      'ems',
      'medicalTreatment',
    ];
    preserveKeys.forEach((k) => {
      const saved = initialStatuses?.[k];
      if (!saved) return;
      const savedIsOverride =
        saved.status === 'UNCERTAIN' ||
        saved.status === 'EXISTS_NOT_HAVE' ||
        saved.status === 'DOES_NOT_EXIST';
      const computedIsConfirmed = next[k].status === 'CONFIRMED';
      if (savedIsOverride && !computedIsConfirmed) {
        next[k] = { ...next[k], status: saved.status };
      }
      if (saved.notes) {
        next[k] = { ...next[k], notes: saved.notes };
      }
    });
    setStatuses(next);

    const subs = computeCoverageSubRows(form);
    const withSubs = initialStatuses as unknown as {
      first_party?: EvidenceStatus;
      health?: EvidenceStatus;
      third_party?: EvidenceStatus;
    };
    const pickSub = (
      computedSub: EvidenceStatus,
      savedSub: EvidenceStatus | undefined,
    ): EvidenceStatus => {
      if (!savedSub) return computedSub;
      if (computedSub.status === 'CONFIRMED') {
        return { ...computedSub, notes: savedSub.notes ?? computedSub.notes };
      }
      const savedIsOverride =
        savedSub.status === 'EXISTS_NOT_HAVE' ||
        savedSub.status === 'DOES_NOT_EXIST' ||
        savedSub.status === 'UNCERTAIN';
      if (savedIsOverride) {
        return { status: savedSub.status, notes: savedSub.notes ?? '' };
      }
      return { ...computedSub, notes: savedSub.notes ?? computedSub.notes };
    };

    setInsuranceSubs({
      pip: pickSub(subs.pip, withSubs.first_party),
      health: pickSub(subs.health, withSubs.health),
      thirdParty: pickSub(subs.thirdParty, withSubs.third_party),
    });
  }, [open, initialStatuses, form]);

  /** Update a single top-level evidence row in local state. */
  const handleChange = (key: EvidenceKey, value: EvidenceStatus) => {
    setStatuses((s) => ({ ...s, [key]: value }));
  };

  return { statuses, handleChange, insuranceSubs, setInsuranceSubs };
};
